import Container from '@/components/Container';

import LessonCard from '@/components/Learn/Lesson';
import DefaultContentTemplateAuthorDetailed from '@/components/DefaultContentTemplate/AuthorDetailed';

// import Card from '';
import DownloadableContent from '@/components/DefaultContentTemplate/DownloadableContent';
import DefaultContentTemplateFaq from '@/components/DefaultContentTemplate/Faq';
import DefaultContentTemplateEmbedded from '@/components/DefaultContentTemplate/Embedded';
import {
  ComponentLearnCourseLesson,
  CourseEntity,
  Enum_Favorite_Type,
  LearnEntity,
  LearnItem,
  LearnPage,
} from '@/graphql/generated/graphql';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchCourseLessonBySlug, fetchRelatedContent, normalizeLearnItems } from '@/services/learn/course.service';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { RelatedContent } from '@/components/DefaultContentTemplate/RelatedContent';
import DefaultContentTemplateAudio from '@/components/DefaultContentTemplate/Audio';
import DefaultContentTemplateMedia from '@/components/DefaultContentTemplate/Media';
import CourseSkeleton from '../course/Course.skeleton';
import { completeLesson, createLearn, fetchCourseLearn, getPage } from '@/services/learn/learn.service';
import classNames from 'classnames';
import CheckIcon from '@/components/Icons/CheckIcon';
import DefaultContentTemplateTextEditor from '@/components/DefaultContentTemplate/TextEditor';
import Button from '@/components/Button';
import { ChevronDoubleLeftIcon } from '@heroicons/react/24/solid';
import { getCurrentLearn } from '@/utils/learn';
import Navigation from '@/components/ui/navigation';
import { navigationCourse } from '../constants';
import Breadcrumb from '@/components/ui/breadcrumb';
import Divider from '@/components/Divider';
import AnimatedFadeInComponent from '@/components/AnimatedFadeInComponent';
import TitleWithActions from '@/components/TitleWithActions';
import DialogConfirmMarkCourseAsCompleted from '../../../components/confirmDialog';

export type PendingLesson = ComponentLearnCourseLesson & {
  idModule: string;
};

function CourseLessonPage() {
  const navigate = useNavigate();
  const [cx, setCourse] = useState<CourseEntity | null>(null);
  const [normalizedLearnItems, setNormalizedLearnItems] = useState<LearnItem[] | null>([]);
  const [relatedContent, setRelatedContent] = useState<Array<RelatedContent> | null>(null);
  const [learn, setLearn] = useState<LearnEntity | null>(null);
  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false);
  const [pendingLessons, setPendingLessons] = useState<PendingLesson[]>([]);
  const [learnPage, setLearnPage] = useState<LearnPage | null>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [loadingDoneLesson, setLoadingDoneLesson] = useState<boolean>(false);
  const { slug, idModule, idLesson } = useParams();

  const c = cx?.attributes;

  const { currentModule, currentLesson, nextLesson, idNextLessonModule, previousIdLesson, previousLessonidModule } =
    getCurrentLearn(c, idModule, idLesson);

  const pendingLessonsFromNormalized = useMemo(() => {
    return cx?.attributes?.modules?.reduce((acc, currModule) => {
      const pendingLessons = (currModule?.lessons || [])
        .filter((lesson) => {
          const isDone = normalizedLearnItems?.some(
            (learnItem) => learnItem?.idModule === currModule?.id && learnItem?.idLesson === lesson?.id,
          );
          return !isDone;
        })
        .map((lesson) => ({ ...lesson, idModule: currModule?.id }));

      return acc.concat(pendingLessons);
    }, [] as unknown[]);
  }, [cx?.attributes?.modules, normalizedLearnItems]);

  const isDoneCurrentLesson = useMemo(
    () =>
      !!normalizedLearnItems?.find((learnItem) => {
        return learnItem?.idModule === currentModule?.id && learnItem?.idLesson === currentLesson?.id;
      }),
    [currentLesson?.id, currentModule?.id, normalizedLearnItems],
  );

  const isLastLesson = useMemo(() => {
    return !nextLesson;
  }, [nextLesson]);

  const lessonsCount = useMemo(
    () =>
      cx?.attributes?.modules?.reduce((acc, curr) => {
        return acc + (curr?.lessons || [])?.length || 0;
      }, 0),
    [cx?.attributes?.modules],
  );

  useEffect(() => {
    (async () => {
      if (!slug) return;
      setLoading(true);

      const [courseResult, relatedContentResult, learnResult, pageResponse] = await Promise.all([
        fetchCourseLessonBySlug(slug),
        fetchRelatedContent(slug),
        fetchCourseLearn(slug),
        getPage(),
      ]);

      setLearnPage(pageResponse.page);
      setCourse(courseResult);
      setRelatedContent(relatedContentResult || []);
      setLearn(learnResult);
      setNormalizedLearnItems(normalizeLearnItems(learnResult as LearnEntity) as LearnItem[]);

      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const createCurrentLearn = useCallback(async () => {
    if (!slug || !cx?.id || learn?.id) {
      return;
    }

    const learnResult = await createLearn(cx.id);
    setLearn(learnResult?.data?.createLearn?.data as LearnEntity);
  }, [cx?.id, slug, learn?.id]);

  const markCourseAsCompleted = useCallback(
    async (skipCheckLesson?: boolean) => {
      if (!slug || !cx?.id || !learn?.id) {
        return;
      }

      if (pendingLessonsFromNormalized && pendingLessonsFromNormalized.length > 0 && !skipCheckLesson) {
        setIsOpenDialog(true);
        setPendingLessons(pendingLessonsFromNormalized as PendingLesson[]);
        return;
      }

      if (!learn?.attributes?.completed) {
        navigate(`/learn/course/${c?.slug}/feedback`);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cx?.id, slug, learn?.id, learn?.attributes?.completed, lessonsCount, normalizedLearnItems],
  );

  const completeCurrentLesson = useCallback(async () => {
    if (!currentModule?.id || !currentLesson?.id || !learn?.id || isDoneCurrentLesson) {
      return null;
    }
    setLoadingDoneLesson(true);

    try {
      const learnResult = await completeLesson(currentLesson.id, currentModule.id, learn.id);

      const studiedItems = normalizeLearnItems(
        learnResult?.data?.createLearnItem?.data?.attributes?.learn?.data as LearnEntity,
      ) as LearnItem[];

      if (!learn?.attributes?.completed && studiedItems?.length === lessonsCount) {
        navigate(`/learn/course/${c?.slug}/feedback`);
      }

      setNormalizedLearnItems(studiedItems);
    } finally {
      setLoadingDoneLesson(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentLesson?.id,
    currentModule?.id,
    isDoneCurrentLesson,
    learn?.attributes?.completed,
    learn?.id,
    lessonsCount,
  ]);

  useEffect(() => {
    if (!cx?.id || !idLesson || !idModule) {
      return;
    }

    createCurrentLearn();
  }, [learn?.id, cx?.id, idLesson, idModule, createCurrentLearn]);

  if (!c || !idModule || !idLesson) {
    return null;
  }

  if (loading) {
    return <CourseSkeleton />;
  }

  const items = [
    {
      label: <>Learn</>,
      url: '/learn',
    },
    {
      label: <>Courses</>,
      url: '/learn',
    },
    { label: c?.title, url: `/learn/course/${c?.slug}` },
    { label: currentLesson?.title },
  ];

  const renderMarkAsDoneButton = () => {
    return (
      <>
        {isLastLesson &&
          isDoneCurrentLesson &&
          (!pendingLessonsFromNormalized || pendingLessonsFromNormalized.length === 0) && (
            <Button
              onClick={() => {
                navigate(`/learn/course/${c?.slug}/feedback`);
              }}>
              {learnPage?.markLessonAsCompleteButton?.markAsCompleteLabel || 'MARK AS COMPLETE'}
            </Button>
          )}
        {(!isLastLesson || !isDoneCurrentLesson) && (
          <Button
            onClick={completeCurrentLesson}
            theme="solid"
            loading={loadingDoneLesson}
            className="flex items-center justify-center text-sh-xxs"
            disabled={isDoneCurrentLesson || loadingDoneLesson}>
            {!loadingDoneLesson && <CheckIcon className="mr-2 inline-block h-4 w-4" />}
            {!isDoneCurrentLesson && (learnPage?.markLessonAsCompleteButton?.markAsCompleteLabel || 'MARK AS COMPLETE')}
            {isDoneCurrentLesson && (learnPage?.markLessonAsCompleteButton?.completedLabel || 'COMPLETED')}
          </Button>
        )}
      </>
    );
  };

  return (
    <>
      <DialogConfirmMarkCourseAsCompleted
        learnPage={learnPage}
        setOpen={setIsOpenDialog}
        open={isOpenDialog}
        lessons={pendingLessons}
        course={c}
      />
      <Navigation title="Learn" items={navigationCourse} />
      <AnimatedFadeInComponent>
        <Breadcrumb items={items} />
      </AnimatedFadeInComponent>
      <Container className="default-content-template min-h-40">
        <div className="col-span-12 lg:col-start-3 lg:col-end-11">
          <div className="grid grid-cols-subgrid gap-sh-30">
            <div className="flex flex-col gap-sh-30">
              <AnimatedFadeInComponent>
                <p className="font-larken text-sh-30-38">{c?.title}</p>
              </AnimatedFadeInComponent>

              {isDoneCurrentLesson ? (
                <AnimatedFadeInComponent>
                  <div className="rounded-2xl border border-sh-blue-light p-5">
                    <h4 className="flex items-center text-sh-md font-bold">
                      <CheckIcon
                        className="mr-2 inline-block h-auto w-4 transition duration-300 ease-in"
                        fill="#1775E3"
                      />
                      {'You completed this lesson'}
                    </h4>
                  </div>
                </AnimatedFadeInComponent>
              ) : null}

              {currentLesson?.highlightContent?.embedded ? (
                <AnimatedFadeInComponent>
                  <DefaultContentTemplateEmbedded
                    className="!my-0"
                    fitContent
                    component={currentLesson?.highlightContent?.embedded}
                    onEnded={() => {
                      completeCurrentLesson();
                    }}
                  />
                </AnimatedFadeInComponent>
              ) : null}

              {currentLesson?.highlightContent?.audio ? (
                <AnimatedFadeInComponent>
                  <DefaultContentTemplateAudio
                    className="!my-0"
                    audio={currentLesson?.highlightContent?.audio}
                    onEnded={() => {
                      completeCurrentLesson();
                    }}
                  />
                </AnimatedFadeInComponent>
              ) : null}

              {currentLesson?.highlightContent?.image ? (
                <AnimatedFadeInComponent>
                  <DefaultContentTemplateMedia
                    component={{
                      id: 'Media',
                      media: currentLesson?.highlightContent?.image?.media,
                    }}
                    className="!my-0"
                  />
                </AnimatedFadeInComponent>
              ) : null}
            </div>
            <div
              className={classNames('grid grid-cols-1 gap-10', {
                'md:grid-cols-[1fr_306px]': (currentModule?.lessons || []).length > 1,
              })}>
              <AnimatedFadeInComponent>
                <div className="pb-4">
                  {cx?.id ? (
                    <TitleWithActions
                      title={currentLesson?.title || ''}
                      entity={cx}
                      entityId={cx?.id || '0'}
                      type={Enum_Favorite_Type.Learn}
                      favorite={false}
                    />
                  ) : null}
                  <p className="mt-4">{currentLesson?.description}</p>
                </div>
              </AnimatedFadeInComponent>

              {(currentModule?.lessons || []).length > 1 ? (
                <div className="border-t border-sh-border-learn md:border-l md:border-t-0 md:pl-10">
                  <ul className="!pl-0 font-aeonik text-sh-xxs">
                    {currentModule?.lessons?.map((lesson) => {
                      const isDone = !!normalizedLearnItems?.find(
                        (learnItem) => learnItem?.idModule === currentModule?.id && learnItem?.idLesson === lesson?.id,
                      );

                      return (
                        <ol
                          className={classNames('!pl-0', {
                            'font-bold': lesson?.id === currentLesson?.id,
                          })}
                          key={lesson?.id}>
                          <AnimatedFadeInComponent>
                            <Link
                              to={`/learn/course/${c?.slug}/module/${currentModule?.id}/lesson/${lesson?.id}`}
                              className="mb-sh-15 flex items-center">
                              {lesson?.title}
                              <div className="ml-1 flex w-4 items-center justify-center text-primary-600">
                                <CheckIcon
                                  className={classNames('inline-block h-auto w-4 transition duration-300 ease-in', {
                                    hidden: !isDone,
                                    visible: isDone,
                                  })}
                                />
                              </div>
                            </Link>
                          </AnimatedFadeInComponent>
                        </ol>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </div>
            {currentLesson?.objectivesSection?.id ? (
              <AnimatedFadeInComponent>
                <div className="rounded-2xl border border-sh-blue-light p-5">
                  <h4 className="mb-4 flex items-center text-sh-md font-bold">
                    {currentLesson?.objectivesSection?.title}
                  </h4>
                  <ul>
                    {currentLesson?.objectivesSection?.objectives?.map((objective, idx) => (
                      <li className="text-sh-sm" key={`objective-${idx}`}>
                        {objective?.description}
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedFadeInComponent>
            ) : null}
            {nextLesson === null ? (
              <AnimatedFadeInComponent>
                <div className="rounded-2xl border border-sh-blue-light p-5">
                  <h4 className="mb-4 flex items-center text-sh-md font-bold">
                    This is the last lesson of this course
                  </h4>
                  <p className="mb-4 text-sh-sm">
                    Congratulations, you reached the end of this course! Have you finished this lesson?
                  </p>
                  <Button
                    theme="solid"
                    disabled={!!learn?.attributes?.completed}
                    onClick={() => {
                      markCourseAsCompleted();
                    }}>
                    {learn?.attributes?.completed
                      ? learnPage?.markCourseAsCompleted?.completedLabel || 'COURSE COMPLETED'
                      : learnPage?.markCourseAsCompleted?.markAsCompleteLabel || 'MARK THIS COURSE AS COMPLETE'}
                  </Button>
                </div>
              </AnimatedFadeInComponent>
            ) : null}

            {currentLesson?.textContent?.markdownText ? (
              <AnimatedFadeInComponent style={{ overflow: 'hidden' }}>
                <DefaultContentTemplateTextEditor
                  className="!my-0"
                  component={{
                    id: 'textContent',
                    markdownText: currentLesson?.textContent?.markdownText || '',
                    markdownHtml: currentLesson?.textContent?.markdownText || '',
                  }}
                />
              </AnimatedFadeInComponent>
            ) : null}

            {(currentLesson?.downloadableContent || []).length > 0 ? (
              <AnimatedFadeInComponent>
                <DownloadableContent
                  className="!my-0"
                  component={{
                    id: 'DownloadableContent',
                    list: currentLesson?.downloadableContent,
                  }}
                  title={learnPage?.contentTemplateLabels?.downloadableContentLabel || undefined}
                />
              </AnimatedFadeInComponent>
            ) : null}

            {(currentLesson?.faq || []).length > 0 ? (
              <AnimatedFadeInComponent>
                <DefaultContentTemplateFaq
                  className="!my-0"
                  text="Enhance your learning"
                  component={{
                    id: 'DefaultContentTemplateFaq',
                    faq: currentLesson?.faq,
                  }}
                />
              </AnimatedFadeInComponent>
            ) : null}
          </div>

          <AnimatedFadeInComponent>
            <div className="mt-sh-30 flex flex-wrap gap-5">
              {previousIdLesson ? (
                <>
                  {renderMarkAsDoneButton()}
                  <Button
                    theme="outline"
                    as="a"
                    href={`/learn/course/${c?.slug}/module/${previousLessonidModule}/lesson/${previousIdLesson}`}>
                    <ChevronDoubleLeftIcon className="inline-block h-4 w-4" />
                    PREVIOUS LESSON
                  </Button>
                </>
              ) : (
                <>{renderMarkAsDoneButton()}</>
              )}
            </div>
          </AnimatedFadeInComponent>

          <AnimatedFadeInComponent>
            <>
              <Divider isBody />

              <DefaultContentTemplateAuthorDetailed
                component={{
                  id: 'DefaultContentTemplateAuthorDetailed',
                  authors: currentLesson?.authors,
                }}
              />
            </>
          </AnimatedFadeInComponent>

          {nextLesson ? (
            <AnimatedFadeInComponent>
              <Divider isBody />
              <p className="mb-sh-30 font-aeonik text-sh-20-28 font-bold">
                {idNextLessonModule === idModule ? 'Next Lesson' : 'Next Module'}
              </p>
              <LessonCard
                onClickButton={() => {
                  completeCurrentLesson();
                }}
                title={nextLesson!.title}
                description={nextLesson!.description}
                thumbnail={nextLesson!.thumbnail}
                tag="Lesson"
                duration={nextLesson?.duration || ''}
                done={
                  !!normalizedLearnItems?.find(
                    (learnItem) => learnItem?.idModule === idNextLessonModule && learnItem?.idLesson === nextLesson?.id,
                  )
                }
                url={`/learn/course/${c?.slug}/module/${idNextLessonModule}/lesson/${nextLesson?.id}`}
              />
            </AnimatedFadeInComponent>
          ) : null}

          {(relatedContent || []).length > 0 ? <></> : null}
          {/* <div>
            <Divider isBody />
            <h3 className="mb-6 text-sh-md font-bold">You might enjoy</h3>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {relatedContent!.map((course) => {
                return <Card {...course} key={course.title} />;
              })}
            </div>
          </div> */}
        </div>
      </Container>
    </>
  );
}

export default CourseLessonPage;
